import { ApiError, type PlatformMe, type SiteContext } from '@smarton-pbx/shared';
import { Building2, Palette } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { api } from '../api';
import { SetPassword } from '../auth/SetPassword';
import { SignIn } from '../auth/SignIn';
import { AccountPage } from '../components/AccountPage';
import { AppShell, BrandMark } from '../components/AppShell';
import { Loading } from '../components/ui';
import { t } from '../i18n';
import { logoUrl } from '../lib/branding';
import { platformHelp } from '../lib/help';
import { DefaultBranding } from './DefaultBranding';
import { NewTenant } from './NewTenant';
import { TenantDetailPage } from './TenantDetail';
import { Tenants } from './Tenants';

const NAME = t('Platform console');

/** Where platform staff create and manage tenants. */
export function PlatformApp({ context }: { context: Extract<SiteContext, { kind: 'platform' }> }) {
  // The platform's default logo (from apps/api/branding, or uploaded under Default branding).
  const logo = context.logoVersion ? logoUrl(context.logoVersion) : null;
  const [me, setMe] = useState<PlatformMe | null>();

  const loadMe = () =>
    api.platform.me().then(setMe, (error: unknown) => {
      if (error instanceof ApiError && error.status === 401) setMe(null);
      else throw error;
    });

  useEffect(() => {
    void loadMe();
  }, []);

  async function signOut() {
    await api.platform.signOut();
    window.location.assign('/');
  }

  return (
    <Routes>
      <Route path="/set-password" element={<SetPassword name={NAME} logoUrl={logo} copyright={context.copyright} setPassword={api.platform.setPassword} />} />
      <Route
        path="*"
        element={
          me === undefined ? (
            <Loading />
          ) : me === null ? (
            <SignIn name={NAME} logoUrl={logo} copyright={context.copyright} signIn={api.platform.signIn} verify={api.platform.verifyMfa} onSignedIn={() => void loadMe()} />
          ) : (
            <AppShell
              brand={<BrandMark name={NAME} logoUrl={logo} />}
              nav={[
                { to: '/', label: t('Tenants'), icon: Building2, end: true },
                { to: '/default-branding', label: t('Default branding'), icon: Palette },
              ]}
              sectionLabel={t('Manage')}
              help={{ docsUrl: context.docsUrl, pages: platformHelp }}
              user={{ name: me.name, detail: t('Platform staff') }}
              accountPath="/account"
              onSignOut={() => void signOut()}
            >
              <Routes>
                <Route path="/" element={<Tenants />} />
                <Route path="/tenants/new" element={<NewTenant />} />
                <Route path="/tenants/:id" element={<TenantDetailPage />} />
                <Route path="/default-branding" element={<DefaultBranding />} />
                <Route
                  path="/account"
                  element={
                    <AccountPage
                      name={me.name}
                      email={me.email}
                      details={t('Platform staff')}
                      mfaEnabled
                      changePassword={api.platform.changePassword}
                    />
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
          )
        }
      />
    </Routes>
  );
}
